/**
 * Sync card — fetch chart of accounts + vendors from Zoho
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useZohoSyncAccounts, useZohoSyncVendors, useZohoStatus } from '@/hooks/useZoho';
import { RefreshCw, BookOpen, Users, Loader2 } from 'lucide-react';

export function ZohoSyncCard() {
  const { t } = useTranslation();
  const status = useZohoStatus();
  const syncAccounts = useZohoSyncAccounts();
  const syncVendors = useZohoSyncVendors();
  const [accountResult, setAccountResult] = useState<string | null>(null);
  const [vendorResult, setVendorResult] = useState<string | null>(null);

  if (!status.data?.connected) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        {t('zoho.sync_disabled', 'Connect to Zoho first to enable sync.')}
      </div>
    );
  }

  const handleSyncAccounts = async () => {
    setAccountResult(null);
    try {
      const r = await syncAccounts.mutateAsync();
      setAccountResult(t('zoho.synced_n_accounts', '{{count}} accounts synced', { count: r.synced }));
    } catch (e) {
      setAccountResult(`✗ ${e instanceof Error ? e.message : 'Failed'}`);
    }
  };

  const handleSyncVendors = async () => {
    setVendorResult(null);
    try {
      const r = await syncVendors.mutateAsync();
      setVendorResult(t('zoho.synced_n_vendors', '{{count}} vendors synced', { count: r.synced }));
    } catch (e) {
      setVendorResult(`✗ ${e instanceof Error ? e.message : 'Failed'}`);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <RefreshCw className="h-5 w-5" />
        {t('zoho.sync_title', 'Sync Zoho data')}
      </h3>
      <p className="text-sm text-muted-foreground">
        {t('zoho.sync_desc', 'Pull your Zoho chart of accounts and vendors so you can map them in Jasr Hub.')}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-md border border-border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">{t('zoho.chart_of_accounts', 'Chart of accounts')}</span>
          </div>
          <Button onClick={handleSyncAccounts} disabled={syncAccounts.isPending} size="sm" variant="outline" className="w-full">
            {syncAccounts.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {t('zoho.sync_now', 'Sync now')}
          </Button>
          {accountResult && <p className="text-xs text-muted-foreground">{accountResult}</p>}
        </div>

        <div className="rounded-md border border-border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">{t('zoho.vendors', 'Vendors')}</span>
          </div>
          <Button onClick={handleSyncVendors} disabled={syncVendors.isPending} size="sm" variant="outline" className="w-full">
            {syncVendors.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {t('zoho.sync_now', 'Sync now')}
          </Button>
          {vendorResult && <p className="text-xs text-muted-foreground">{vendorResult}</p>}
        </div>
      </div>
    </div>
  );
}
